import { type CSSProperties, useCallback, useState } from "react";
import { BoxIcon, MessageSquareTextIcon } from "lucide-react";

import { viewSupportsNodePanel } from "@/app/app/_/layouts/nodePanelConfig";
import { Icon } from "@/components/atoms/Icon";
import { State } from "@/state";
import { Button } from "@/components/ui/button";
import { ButtonGroup } from "@/components/ui/button-group";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/components/ui/utils";

const TOGGLE_LABEL_WIDTHS = {
  Details: "4.25rem",
  Thread: "3.75rem",
} as const;

/**
 * Shut, a toggle is its icon with a toggle group's room either side (``px-3``)
 * - the table or cards toggle's under it - and nothing beside: the room
 * before its words is theirs (``ms-1.5``), come and gone with them, not a gap
 * held open beside nothing.
 */
const PANEL_TOGGLE_CLASS = cn(
  "gap-0 has-[>svg]:px-3",
  "hover:text-foreground",
  "[&_.panel-toggle-label]:inline-block [&_.panel-toggle-label]:overflow-hidden [&_.panel-toggle-label]:whitespace-nowrap [&_.panel-toggle-label]:align-bottom",
  "[&_.panel-toggle-label]:[transition:max-width_200ms_ease-in-out,opacity_200ms_ease-in-out,margin-inline-start_200ms_ease-in-out] motion-reduce:[&_.panel-toggle-label]:[transition:none]",
);

/**
 * Where its panel is open or shut: its colour and how much of its words show -
 * and under the pointer (not just after it was clicked) its words peeking out,
 * or drawing back.
 */
function panelToggleClassName(isOpen: boolean) {
  return isOpen
    ? cn(
        PANEL_TOGGLE_CLASS,
        "text-foreground",
        "[&_.panel-toggle-label]:ms-1.5 [&_.panel-toggle-label]:max-w-(--label-full-width) [&_.panel-toggle-label]:opacity-100",
        "[&:hover:not(:disabled):not([data-suppress-hover-preview])_.panel-toggle-label]:max-w-[calc(var(--label-full-width)*0.75)] [&:hover:not(:disabled):not([data-suppress-hover-preview])_.panel-toggle-label]:opacity-75",
      )
    : cn(
        PANEL_TOGGLE_CLASS,
        "text-muted-foreground",
        "[&_.panel-toggle-label]:ms-0 [&_.panel-toggle-label]:max-w-0 [&_.panel-toggle-label]:opacity-0",
        "[&:hover:not(:disabled):not([data-suppress-hover-preview])_.panel-toggle-label]:ms-1.5 [&:hover:not(:disabled):not([data-suppress-hover-preview])_.panel-toggle-label]:max-w-[calc(var(--label-full-width)*0.25)] [&:hover:not(:disabled):not([data-suppress-hover-preview])_.panel-toggle-label]:opacity-25",
      );
}

function PanelToggleLabel({
  children,
  isOpen,
}: {
  children: React.ReactNode;
  isOpen: boolean;
}) {
  return (
    <span className="panel-toggle-label" aria-hidden={!isOpen}>
      {children}
    </span>
  );
}

function usePanelToggleHoverPreview() {
  const [suppressHoverPreview, setSuppressHoverPreview] = useState(false);

  const onMouseLeave = useCallback(() => {
    setSuppressHoverPreview(false);
  }, []);

  const wrapClick = useCallback((onClick: () => void) => {
    return () => {
      setSuppressHoverPreview(true);
      onClick();
    };
  }, []);

  return {
    suppressHoverPreview,
    onMouseLeave,
    wrapClick,
  };
}

export function AppTopbarPanelToggles({
  disableOpen,
}: {
  disableOpen: boolean;
}) {
  const activeAppView = State((store) => store.activeAppView);
  const isNodePanelOpen = State((store) => store.isNodePanelOpen);
  const setIsNodePanelOpen = State((store) => store.setIsNodePanelOpen);
  const isThreadPanelOpen = State((store) => store.isThreadPanelOpen);
  const setIsThreadPanelOpen = State((store) => store.setIsThreadPanelOpen);
  const setSnackbarMessage = State((store) => store.setSnackbarMessage);

  const detailsHover = usePanelToggleHoverPreview();
  const threadHover = usePanelToggleHoverPreview();

  const isThreadsView = activeAppView === "thread";
  const showNodeToggle = viewSupportsNodePanel(activeAppView);
  const threadDisabled = disableOpen && !isThreadPanelOpen && !isThreadsView;
  const threadActive = isThreadPanelOpen && !isThreadsView;

  const nodeTooltip = isNodePanelOpen ? "Close details" : "Open details";
  const threadTooltip = isThreadPanelOpen ? "Close thread" : "Open thread";

  const threadButton = (
    <Button
      variant="outline"
      size="sm"
      id="panel-toggle-button"
      onClick={threadHover.wrapClick(() => {
        if (isThreadsView) {
          setSnackbarMessage("You're already on the Threads view");
          return;
        }
        setIsThreadPanelOpen(!isThreadPanelOpen);
      })}
      onMouseLeave={threadHover.onMouseLeave}
      disabled={threadDisabled}
      aria-label="Thread"
      data-suppress-hover-preview={
        threadHover.suppressHoverPreview ? "" : undefined
      }
      className={panelToggleClassName(threadActive)}
      style={
        { "--label-full-width": TOGGLE_LABEL_WIDTHS.Thread } as CSSProperties
      }
    >
      <Icon ReactIcon={MessageSquareTextIcon} size="sm" />
      <PanelToggleLabel isOpen={threadActive}>Thread</PanelToggleLabel>
    </Button>
  );

  return (
    <ButtonGroup>
      {showNodeToggle && (
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              id="details-panel-toggle-button"
              onClick={detailsHover.wrapClick(() =>
                setIsNodePanelOpen(!isNodePanelOpen),
              )}
              onMouseLeave={detailsHover.onMouseLeave}
              aria-label="Details"
              data-suppress-hover-preview={
                detailsHover.suppressHoverPreview ? "" : undefined
              }
              className={panelToggleClassName(isNodePanelOpen)}
              style={
                {
                  "--label-full-width": TOGGLE_LABEL_WIDTHS.Details,
                } as CSSProperties
              }
            >
              <Icon ReactIcon={BoxIcon} size="sm" />
              <PanelToggleLabel isOpen={isNodePanelOpen}>
                Details
              </PanelToggleLabel>
            </Button>
          </TooltipTrigger>
          <TooltipContent side="bottom">{nodeTooltip}</TooltipContent>
        </Tooltip>
      )}
      {isThreadsView ? (
        threadButton
      ) : (
        <Tooltip>
          <TooltipTrigger asChild>{threadButton}</TooltipTrigger>
          <TooltipContent side="bottom">{threadTooltip}</TooltipContent>
        </Tooltip>
      )}
    </ButtonGroup>
  );
}
